// Maps drawn as SVG in React, the way the report draws them (10-serverless/120-pack/pack_maps.py):
// no basemap tiles, the UK in light grey (public/maps/uk-context.geojson), council districts outlined
// (public/maps/councils.geojson), cities and towns from public/maps/places.json, and the console's
// lime pin. The same files the report's maps use, so the page and its PDF export match.
//
//   loadMapData()       the three files, fetched once per page load and shared
//   Proj                lat/lng → SVG x/y (equirectangular, longitude scaled by cos(latitude))
//   ringsPath()         a GeoJSON polygon as an SVG path
//   Labels              greedy label placement: a label that would overlap is dropped
//   textWidth()         a label's approximate width, for the placement
//   nationOf()          a district's nation from its ONS code (E/W/S/N)

export interface MapPlace {
  name: string;
  lat: number;
  lng: number;
  rank: number;
}

export interface MapData {
  context: GeoJSON.FeatureCollection | null;
  councils: GeoJSON.FeatureCollection;
  places: { places: MapPlace[]; counties: { name: string; lat: number; lng: number }[] };
}

// Fetched once and kept: the boundary files are large (~0.5 MB together) and never change mid-session
let loading: Promise<MapData> | null = null;

/** The boundary and place files, shared by every map on the page. */
export function loadMapData(): Promise<MapData> {
  if (!loading) {
    loading = Promise.all([
      fetch("/maps/uk-context.geojson")
        .then((r) => r.json())
        .catch(() => null),
      fetch("/maps/councils.geojson").then((r) => r.json()),
      fetch("/maps/places.json").then((r) => r.json()),
    ]).then(([context, councils, places]) => ({ context, councils, places }));
    // A failure is tried again next time rather than remembered
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
}

// The console's map colours (as pack_maps.py)
export const MAP = { land: "#e5e4e2", edge: "#9a9a9a", districtEdge: "#8e959e", navy: "#202937", lime: "#E1EE2B", text: "#4b5563", sea: "#ffffff" };

/** lat/lng → SVG x/y: equirectangular, longitude scaled by cos(latitude) so shapes look right; the extent centred in the frame. */
export class Proj {
  k: number;
  s: number;
  ox: number;
  oy: number;
  constructor(south: number, west: number, north: number, east: number, w: number, h: number) {
    this.k = Math.cos((((south + north) / 2) * Math.PI) / 180);
    const sx = w / ((east - west) * this.k);
    const sy = h / (north - south);
    this.s = Math.min(sx, sy);
    this.ox = (w - (east - west) * this.k * this.s) / 2 - west * this.k * this.s;
    this.oy = (h - (north - south) * this.s) / 2 + north * this.s;
  }
  xy(lat: number, lng: number): [number, number] {
    return [lng * this.k * this.s + this.ox, this.oy - lat * this.s];
  }
}

/** Every ring of a Polygon or MultiPolygon. */
function rings(geom: GeoJSON.Geometry): number[][][] {
  if (geom.type === "Polygon") return geom.coordinates;
  if (geom.type === "MultiPolygon") return geom.coordinates.flat();
  return [];
}

/** A polygon as an SVG path, through the projection. */
export function ringsPath(geom: GeoJSON.Geometry, pr: Proj): string {
  return rings(geom)
    .map(
      (ring) =>
        "M" +
        ring
          .map(([lng, lat]) => {
            const [x, y] = pr.xy(lat, lng);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join("L") +
        "Z",
    )
    .join("");
}

/** Manrope's average glyph is about 0.58 of its size; tracked capitals a little wider. */
export const textWidth = (text: string, size: number, spacing = 0) => text.length * (size * 0.58 + spacing);

/** Greedy label placement: a label is dropped if it overlaps one already placed, a kept-clear box, or the edge. */
export class Labels {
  boxes: [number, number, number, number][];
  constructor(
    public w: number,
    public h: number,
    blocked: [number, number, number, number][] = [],
  ) {
    this.boxes = [...blocked];
  }
  fits(x1: number, y1: number, x2: number, y2: number): boolean {
    if (x1 < 4 || y1 < 4 || x2 > this.w - 4 || y2 > this.h - 4) return false;
    if (this.boxes.some(([a, b, c, d]) => !(x2 < a || x1 > c || y2 < b || y1 > d))) return false;
    this.boxes.push([x1, y1, x2, y2]);
    return true;
  }
}

/** The console's pin as SVG path data (28 × 38, its point at 14,37). */
export const PIN_PATH = "M14 1C7 1 1.5 6.4 1.5 13.3 1.5 22.5 14 37 14 37S26.5 22.5 26.5 13.3C26.5 6.4 21 1 14 1Z";

/** A district's nation from its ONS code's first letter; null when it can't tell. */
export function nationOf(code?: string | null): string | null {
  const first = (code ?? "").charAt(0);
  return ({ E: "England", W: "Wales", S: "Scotland", N: "Northern Ireland" } as Record<string, string>)[first] ?? null;
}

/** The district's ONS code, by its name, from the councils file. */
export function districtCode(councils: GeoJSON.FeatureCollection, name?: string | null): string | null {
  if (!name) return null;
  const f = councils.features.find((x) => x.properties?.name === name);
  return (f?.properties?.code as string | undefined) ?? null;
}
